import { useEffect, useMemo, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useClinic } from '@/components/Context/ClinicaContext';
import { createTarefa } from '@/services/tarefaService';
import { listChatContacts } from '../contactsService';

export const TAREFA_STATUS = {
  DODIA: 'DODIA',
  PENDENTE: 'PENDENTE',
  EM_PROGRESSO: 'EM_PROGRESSO',
  CONCLUIDA: 'CONCLUIDA',
};

export const TAREFA_PERIODICIDADE = {
  NENHUMA: 'NENHUMA',
  DIARIA: 'DIARIA',
  SEMANAL: 'SEMANAL',
  MENSAL: 'MENSAL',
};

function dateToLocalDateTimeString(dateStr) {
  const d = String(dateStr || '').trim();
  if (!d) return null;
  return `${d}T00:00:00`;
}

export function useTarefaModal({ isOpen, onClose, clinicaId, onCreated }) {
  const toast = useToast();
  const { selectedClinic } = useClinic();

  const [selected, setSelected] = useState(null);
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [dataFinal, setDataFinal] = useState('');
  const [horario, setHorario] = useState('');
  const [periodicidade, setPeriodicidade] = useState(TAREFA_PERIODICIDADE.NENHUMA);
  const [diasSemana, setDiasSemana] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) setSelected(null);
  }, [isOpen]);

  const canSave = useMemo(() => {
    const t = titulo.trim();
    const okBase = !!clinicaId && !!selected?.value && t.length > 0 && t.length <= 120;
    if (!okBase) return false;
    if (periodicidade === TAREFA_PERIODICIDADE.SEMANAL) return diasSemana.length > 0;
    return true;
  }, [clinicaId, selected?.value, titulo, periodicidade, diasSemana]);

  const handleClose = () => {
    setSelected(null);
    setTitulo('');
    setDescricao('');
    setDataFinal('');
    setHorario('');
    setPeriodicidade(TAREFA_PERIODICIDADE.NENHUMA);
    setDiasSemana([]);
    onClose?.();
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await createTarefa({
        clinicaId,
        enviadoPara: selected?.value,
        enviadoParaTipo: selected?.role !== 'Socio' ? 'User' : 'Socio',
        titulo: titulo.trim(),
        descricao: descricao.trim() ? descricao.trim() : undefined,
        periodicidade,
        diasSemana: periodicidade === TAREFA_PERIODICIDADE.SEMANAL ? diasSemana : undefined,
        dataFinal: dateToLocalDateTimeString(dataFinal),
        horario: String(horario || '').trim() ? String(horario).trim() : null,
      });
      toast({ title: 'Tarefa criada', status: 'success', duration: 2500, isClosable: true });
      onCreated?.();
      handleClose();
    } catch (e) {
      console.error('Erro ao criar tarefa:', e);
      toast({
        title: 'Erro ao criar',
        description: e?.message || 'Não foi possível criar a tarefa',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  // Usuários e sócios da clínica — mesma fonte usada no chat, já retorna os dois tipos
  // combinados (role: 'Socio' para sócios) num único formato.
  const { data: contacts } = useQuery({
    queryKey: ['lista_users_socios', selectedClinic?._id],
    queryFn: () => listChatContacts({ clinicaId: selectedClinic._id }),
    enabled: !!selectedClinic?._id,
    refetchOnWindowFocus: false,
  });

  const userOptions = useMemo(
    () => (contacts || []).map((c) => ({ value: c.id, label: c.label || c.name, role: c.role })),
    [contacts]
  );

  const selectedOption = selected ? { value: selected.value, label: selected.label, role: selected.role } : null;

  const handlePeriodicidadeChange = (next) => {
    setPeriodicidade(next);
    if (next !== TAREFA_PERIODICIDADE.SEMANAL) setDiasSemana([]);
    if (next === TAREFA_PERIODICIDADE.SEMANAL && diasSemana.length === 0) {
      setDiasSemana([new Date().getDay()]);
    }
  };

  const handleDiaSemanaChange = (value, checked) => {
    setDiasSemana((prev) => {
      if (checked) return Array.from(new Set([...prev, value]));
      return prev.filter((x) => x !== value);
    });
  };

  return {
    selected,
    setSelected,
    titulo,
    setTitulo,
    descricao,
    setDescricao,
    dataFinal,
    setDataFinal,
    horario,
    setHorario,
    periodicidade,
    diasSemana,
    saving,
    canSave,
    userOptions,
    selectedOption,
    handleClose,
    handleSave,
    handlePeriodicidadeChange,
    handleDiaSemanaChange,
  };
}
